import { useEffect, useMemo, useState } from "react";
import Modal from "../Modal";
import { useMutation } from "@tanstack/react-query";
import { Button } from "../Button";
import { Field } from "../../pages/certifications/components/CertificationFormModal";
import { toast } from "react-hot-toast";
import { ShowGenericErrorToast } from "../../services/toast";
import { EntityItem, EntityApi, EntityLabels, getEntityLabels } from "./types";

export function EntityFormModal({
  isOpen,
  onClose,
  item,
  onSuccess,
  api,
  labels,
}: {
  isOpen: boolean;
  onClose: () => void;
  item?: EntityItem | null;
  onSuccess: () => void;
  api: EntityApi;
  labels: EntityLabels;
}) {
  const [form, setForm] = useState({ name: item?.name ?? "" });
  const [isLoading, setIsLoading] = useState(false);

  const { mutate, isLoading: isLoadingMutation } = useMutation({
    mutationFn: (data: { name: string }) => api.create(data),
  });

  const { mutate: update, isLoading: isLoadingUpdate } = useMutation({
    mutationFn: (data: { id?: string; name: string }) =>
      api.update(data.id!, { id: data.id, name: data.name }),
  });

  const isEdit = !!item;
  const { createTitle, editTitle, createButton, updateButton, addedToast, updatedToast } =
    getEntityLabels(labels);

  const handleSave = () => {
    if (!form.name) return;
    setIsLoading(true);
    const payload = { id: item?.id, name: form.name };
    const onDone = () => {
      setIsLoading(false);
      onSuccess();
      setForm({ name: "" });
    };
    const onErr = () => {
      setIsLoading(false);
      ShowGenericErrorToast();
    };
    if (item?.id) {
      update(payload, {
        onSuccess: () => {
          onDone();
          toast.success(updatedToast);
        },
        onError: onErr,
      });
    } else {
      mutate({ name: form.name }, {
        onSuccess: () => {
          onDone();
          toast.success(addedToast);
        },
        onError: onErr,
      });
    }
  };

  const isSaving = isLoading || isLoadingMutation || isLoadingUpdate;
  const isDisabled = useMemo(() => !form.name, [form.name]);

  useEffect(() => {
    if (item) {
      setForm((prev) => ({ ...prev, name: item.name }));
    } else {
      setForm((prev) => ({ ...prev, name: "" }));
    }
  }, [item, isOpen]);

  return (
    <Modal
      contentClassName="!px-0"
      title={isEdit ? editTitle : createTitle}
      isOpen={isOpen}
      handleModal={onClose}
    >
      <div className="max-h-[calc(100vh-125px)] overflow-auto">
        <div className="flex flex-col gap-4 px-5 mt-2">
          <div className="flex gap-4 items-start">
            <Field label="Name" className="flex-1">
              <input
                type="text"
                className="border border-[#E9E9E9] rounded px-2 py-1 !outline-none"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </Field>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              variant="primary"
              className="h-[58px] !rounded-full"
              loading={isSaving}
              disabled={isDisabled}
            >
              {isEdit ? updateButton : createButton}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
